import {
  CreatePointAdjustmentPayload,
  MAX_POINT_ADJUSTMENT_INTERNAL_REASON,
  MAX_POINT_ADJUSTMENT_POINTS,
  MAX_POINT_ADJUSTMENT_USER_REASON,
} from "../../../models/pointLedger";

/**
 * El estado y las reglas del formulario de movimiento manual, sin React.
 *
 * SE SEPARA DEL DIÁLOGO porque es la parte que más conviene poder recorrer entera en tests:
 * acá se decide si lo que el admin escribió mueve un saldo ajeno, y el error más caro
 * —cargar una resta como suma— es un error de traducción entre lo que se ve y lo que viaja.
 */

/** Lo que el admin elige con dos botones. En el payload esto es el SIGNO de `points`. */
export type PointAdjustmentDirection = "add" | "subtract";

export interface PointAdjustmentFormValues {
  /** `null` = todavía no eligió, y sólo pasa cuando el SUPER_ADMIN está en "Todas". */
  tenantId: string | null;
  email: string;
  direction: PointAdjustmentDirection;
  /** SIEMPRE POSITIVO: el signo sale de `direction`. `null` = el campo está vacío. */
  points: number | null;
  internalReason: string;
  userReason: string;
}

export interface PointAdjustmentFormErrors {
  tenantId?: string;
  email?: string;
  points?: string;
  internalReason?: string;
  userReason?: string;
}

export const buildInitialPointAdjustmentValues = (
  tenantId: string | null
): PointAdjustmentFormValues => ({
  tenantId,
  email: "",
  // Sumar por defecto: restarle puntos a alguien es la acción menos frecuente y la más
  // difícil de deshacer, así que no es la que tiene que estar elegida de arranque.
  direction: "add",
  points: null,
  internalReason: "",
  userReason: "",
});

/**
 * Un mail con forma de mail. No valida que exista —eso lo sabe el backend, que además exige
 * que la persona pertenezca a la institución—, sólo evita el viaje por un error de tipeo.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validatePointAdjustmentForm(
  values: PointAdjustmentFormValues
): PointAdjustmentFormErrors {
  const errors: PointAdjustmentFormErrors = {};

  if (values.tenantId === null) {
    errors.tenantId = "Elegí la institución de la persona.";
  }

  const email = values.email.trim();
  if (email === "") {
    errors.email = "Escribí el mail de la persona.";
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = "Ese mail no tiene forma de mail.";
  }

  if (values.points === null || values.points === 0) {
    // Cero no es un movimiento: la base lo rechaza y además no explicaría ningún cambio de
    // saldo en el historial de la persona.
    errors.points = "Poné cuántos puntos sumar o restar.";
  } else if (!Number.isInteger(values.points) || values.points < 0) {
    errors.points = "Tiene que ser un número entero positivo: el signo lo elegís arriba.";
  } else if (values.points > MAX_POINT_ADJUSTMENT_POINTS) {
    errors.points = `El máximo por movimiento es ${MAX_POINT_ADJUSTMENT_POINTS} puntos.`;
  }

  const internalReason = values.internalReason.trim();
  if (internalReason === "") {
    errors.internalReason = "El motivo interno es obligatorio.";
  } else if (internalReason.length > MAX_POINT_ADJUSTMENT_INTERNAL_REASON) {
    errors.internalReason = `Máximo ${MAX_POINT_ADJUSTMENT_INTERNAL_REASON} caracteres.`;
  }

  const userReason = values.userReason.trim();
  if (userReason === "") {
    errors.userReason = "Escribí qué va a leer la persona en su historial.";
  } else if (userReason.length > MAX_POINT_ADJUSTMENT_USER_REASON) {
    errors.userReason = `Máximo ${MAX_POINT_ADJUSTMENT_USER_REASON} caracteres.`;
  }

  return errors;
}

/**
 * El payload, con el signo ya aplicado.
 *
 * Devuelve `null` si el formulario no es válido: la traducción de "restar 50" a `-50` no
 * puede pasar por un estado intermedio donde `points` sea `null`, porque un `0` por descarte
 * sería un movimiento que el backend rechaza y, peor, un `50` positivo sería una suma cargada
 * como resta.
 */
export function buildCreatePointAdjustmentPayload(
  values: PointAdjustmentFormValues
): CreatePointAdjustmentPayload | null {
  if (Object.keys(validatePointAdjustmentForm(values)).length > 0) return null;
  if (values.points === null) return null;

  const magnitude = Math.abs(values.points);

  return {
    tenantId: values.tenantId,
    email: values.email.trim(),
    points: values.direction === "subtract" ? -magnitude : magnitude,
    internalReason: values.internalReason.trim(),
    userReason: values.userReason.trim(),
  };
}

/** El resumen que confirma lo que se hizo, con el saldo que le quedó a la persona. */
export function formatAdjustmentSuccess(
  points: number,
  fullName: string,
  balanceAfter: number
): string {
  const verb = points < 0 ? "Restaste" : "Sumaste";
  return `${verb} ${Math.abs(points)} puntos a ${fullName}. Saldo: ${balanceAfter}.`;
}
